
import axios from "axios";
import { BASE_API } from "./entities";

// ── ports cache (localStorage) ──────────────────────────────────────────────
// The Betty port-list fetch is paginated + slow (~hundreds of rows). Cache the mapped
// result so repeat app loads are instant; refetch only after the TTL. Bump CACHE_VERSION
// when the mapped shape changes (e.g. coords added). For a build-time/periodic prebake,
// drop a JSON at a Netlify URL and seed this same cache from it.
const CACHE_VERSION = 3; // v3 = lat/lng merged from a direct allCompany coords query
const CACHE_TTL_MS = 12 * 60 * 60 * 1000; // 12h
const cacheKey = (isUserPresent) => `pf_ports_cache_v${CACHE_VERSION}_${isUserPresent ? "user" : "public"}`;

const readPortsCache = (isUserPresent) => {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    const raw = window.localStorage.getItem(cacheKey(isUserPresent));
    if (!raw) return null;
    const { ts, data } = JSON.parse(raw);
    if (!ts || Date.now() - ts > CACHE_TTL_MS) return null; // stale
    return data;
  } catch (e) {
    return null; // corrupt/unavailable cache — fall through to live fetch
  }
};

const writePortsCache = (isUserPresent, data) => {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    window.localStorage.setItem(cacheKey(isUserPresent), JSON.stringify({ ts: Date.now(), data }));
  } catch (e) {
    /* quota / unavailable — caching is best-effort */
  }
};

// The port-list Betty actions (38f1c7a2…/ad1afac9…) don't emit latitude/longitude, so we
// fetch coords directly from Company (Phase 1 wrote them) and merge by id. Best-effort:
// any failure (e.g. a token without direct-read access) leaves coords null → the picker
// falls back to name/country matching. Paginated at the runtime's take cap.
const PORT_LIST_FILTER =
  "{ _and: [ { excludeFromPortList: { eq: false } }, { _or: [ { isPort: { eq: true } }, { isPortManual: { eq: true } } ] } ] }";

const get_ports_coords = async (jwt_token) => {
  const headers = { "Content-Type": "application/json", Authorization: "Bearer " + jwt_token };
  const byId = {};
  for (let skip = 0; ; skip += 100) {
    let res;
    try {
      const query = `{ allCompany(take: 100, skip: ${skip}, where: ${PORT_LIST_FILTER}) { results { id latitude longitude } } }`;
      res = await axios.post(BASE_API, { query }, { headers });
    } catch (e) {
      console.warn("[ports coords] direct read failed, using name/country fallback:", e?.message);
      return byId; // caller keeps null coords on failure
    }
    const results = res?.data?.data?.allCompany?.results;
    if (!Array.isArray(results)) return byId;
    results.forEach((r) => {
      if (r.latitude != null) byId[r.id] = { latitude: r.latitude, longitude: r.longitude };
    });
    if (results.length < 100) return byId;
  }
};

// Netlify-prebaked static asset (built periodically) → { port_origin, port_destination }.
const PREBAKED_PORTS_URL = `${process.env.PUBLIC_URL || ""}/geo/ports.json`;
const readPrebakedPorts = async () => {
  try {
    const res = await axios.get(PREBAKED_PORTS_URL, { timeout: 8000 });
    return res?.data ? res.data : null;
  } catch (e) {
    return null; // not deployed / not built yet — fall through to live Betty
  }
};

export const get_all_ports_data = async (jwt_token, isUserPresent) =>{
   const headers = {
    'Content-Type': 'application/json',
    'Authorization': "Bearer " + jwt_token + ""
  }

  const actionId = isUserPresent
    ? "38f1c7a24c854a1baa00213b0f564eb1"
    : "ad1afac9a115464c8f7cafd6137614fb";

  const PAGE_SIZE = 200;
  let allResults = [];
  let skip = 0;

  while (true) {
    const query = {
      query: "mutation { action(id: $action_id input: $input) }",
      variables: {
        action_id: actionId,
        input: { skip, take: PAGE_SIZE },
      },
    };

    let response;
    try {
      response = await axios.post(BASE_API, JSON.stringify(query), { headers });
    } catch (err) {
      return [403, []];
    }

    if (response.status !== 200 && response.status !== 201) return [403, []];

    const results = response?.data?.data?.action?.results;
    if (!results) return [403, []];

    allResults = allResults.concat(results);

    if (results.length < PAGE_SIZE) break;
    skip += PAGE_SIZE;
  }

  return [200, allResults];
}

export const get_all_ports_from_db = async (getstate) => {
  const isUserDetailsValid = () => {
    const userDetails = getstate().route.USER_DETAILS;
    return userDetails !== null && userDetails !== undefined && 
           (typeof userDetails === 'object' ? Object.keys(userDetails).length > 0 : userDetails.trim().length > 0);
  };

  const isUserPresent = isUserDetailsValid();

  // 1) localStorage cache — instant on repeat loads.
  const cached = readPortsCache(isUserPresent);
  if (cached) return { status: "200", response: cached };

  // 2) cold-start: a Netlify-prebaked static asset (fast CDN) seeds the cache before we
  //    ever hit Betty live. Best-effort — falls through to the live fetch on any miss.
  const prebaked = await readPrebakedPorts();
  if (prebaked && prebaked.port_origin && prebaked.port_origin.Ports && prebaked.port_origin.Ports.length) {
    writePortsCache(isUserPresent, prebaked);
    return { status: "200", response: prebaked };
  }

  let jwt_token;
  if(isUserPresent){
    jwt_token = getstate().route.CLIENT_TOKEN.JWT_TOKEN;
  }else{
    jwt_token = getstate().route.JWT_TOKEN.JWT_TOKEN
  }
  
  if (jwt_token !== "" || jwt_token !== undefined) {
    const response = await get_all_ports_data(jwt_token, isUserPresent);
    // console.log("-response: ",response)
    let final_ports = [];
    let port_from = []
    let port_to = []
    if (response && response?.[0] === 200) {
        if (response[1] && response[1].length > 1) {
          const final_ports_from_bettyblocks = response[1]
          final_ports_from_bettyblocks.forEach((object, index) => {
              if (object.inCountry != null) {
                  port_to.push(object)
                  port_from.push(object)
              }
          })
          final_ports = await separate_data(port_from, port_to)
          // merge coords (the port-list action omits lat/lng; Company has them post-Phase-1)
          const coordsById = await get_ports_coords(jwt_token)
          const applyCoords = (ports) => ports.forEach((p) => {
            const c = coordsById[p.id]
            if (c) { p.latitude = c.latitude; p.longitude = c.longitude }
          })
          applyCoords(final_ports.port_origin.Ports)
          applyCoords(final_ports.port_destination.Ports)
          // cache the freshly-mapped (coord-enriched) ports for instant subsequent loads
          if (final_ports.port_origin?.Ports?.length) {
            writePortsCache(isUserPresent, final_ports);
          }
        }
    }else{
        console.error("ports data not available.")
    }
    // console.log("ports data.....",final_ports)
    return {
        "status": "200",
        "response": final_ports
    }
  } else {
      return {
          "status": "404",
          "error": "No JWT found"
      }
  }
}

/**
 * @function
 * @name separate_data
 * @description  will take origin port and destination port, and map it as per the format of redux store
 * @returns {Redux.Action} Will return object containing port origin and port destination
 */
const separate_data = async (port_from, port_to) => {
  let port_destination = {
    Ports: [],
  };
  let port_origin = {
    Ports: [],
  };
  // One mapper for both sides (DRY). latitude/longitude flow through once the port-list
  // Betty action (38f1c7a2…/ad1afac9…) includes them in its output; until then they are
  // null and the picker falls back to name/country match (no distance). PRD §6 / UX §6.
  const toPort = (object) => ({
    id: object.id,
    Name: object.companyName,
    Country: object.inCountry.companyName,
    CountryId: object.inCountry.id,
    linkedPort: object?.linkedPort,
    portLinked: object?.portLinked,
    portCode: object?.scacCode,
    latitude: object?.latitude ?? null,
    longitude: object?.longitude ?? null,
  });

  port_to.forEach((object) => port_destination.Ports.push(toPort(object)));
  port_from.forEach((object) => port_origin.Ports.push(toPort(object)));

  return {
    port_origin: port_origin,
    port_destination: port_destination,
  };
};